// VaultMenu.tsx
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsIcon from '@mui/icons-material/Collections';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import GifBoxIcon from '@mui/icons-material/GifBox';
import ImageIcon from '@mui/icons-material/Image';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import UploadIcon from '@mui/icons-material/Upload';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
    Box,
    Card,
    debounce,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper,
    SelectChangeEvent,
    Slide,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import pLimit from 'p-limit';
import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { GridOnItemsRenderedProps, FixedSizeGrid as GridVirt } from 'react-window';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import useSubscriberInit from '../../hooks/useSubscriberInit';
import useSubscriberUpload, { OnlyFansFileUploadResponse } from '../../hooks/useSubscriberUpload';
import useSubscriberVaultAlbums, { OnlyFansVaultAlbums, OnlyFansVaultMedias } from '../../hooks/useSubscriberVaultAlbums';
import useSubscriberVaultMedias, { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import { useDownloadManager } from '../../store/downloadManager/useDownloadManager';
import { UserContext } from '../../store/UserContext';
import { isSextforceActive } from '../../utils/common';
import {
    buildUploadAcceptAttr,
    FALLBACK_UPLOAD_TYPES,
    isAllowedUploadFile,
    resolveUploadMimeType,
    withHeicUploadTypes,
} from '../../utils/uploadTypes';
import StyledLocalMenu from '../common/StyledLocalMenu';
import MessageUsersFormAddMediasSelectorGoToDateDialog from '../services/messageUsers/sendMessage/MessageUsersFormAddMediasSelectorGoToDateDialog';
import AlbumSelect from './AlbumSelect';
import VaultTile from './VaultTile';
import VaultUploadDialog, { MAX_VAULT_UPLOAD_FILES, UploadFileMetadata } from './VaultUploadDialog';

export interface VaultMenuProps {
    subscriber: OnlyFansSubscriber;
    buttonText?: string;
    onItemClick?: (item: VaultMediaResponseList) => void;
    editOnly: boolean;
    selectedItems?: VaultMediaResponseList[];
    selectAllItems?: (items: VaultMediaResponseList[]) => void;
    clearAllItems?: () => void;
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    onOpenChange?: (open: boolean) => void;
    setSelectedAlbum?: React.Dispatch<React.SetStateAction<number>>;
    refreshContent?: boolean;
    // When hosted in a fixed-height flex container (e.g. a dialog), grow to fill it
    // and size the virtualized grid from the measured container instead of the window,
    // so the grid is the only scroll region.
    fillHeight?: boolean;
    // Extra buttons rendered in the hovering selection bar (shown while items are selected).
    selectionActions?: React.ReactNode;
}

type CellData = {
    items: VaultMediaResponseList[];
    selectedSet: Set<number>;
    handleMediaClicked: (item: VaultMediaResponseList) => void;
    CELL: number;
    GAP: number;
    COLS: number;
    allowDownload: boolean;
    subscriber: OnlyFansSubscriber;
};

const CellRenderer: React.FC<{
    columnIndex: number;
    rowIndex: number;
    style: React.CSSProperties;
    data: CellData;
}> = ({ columnIndex, rowIndex, style, data }) => {
    const { items, selectedSet, allowDownload, subscriber, handleMediaClicked, CELL, GAP, COLS } = data;
    const idx = rowIndex * COLS + columnIndex;
    const item = items[idx];

    const cellStyle: React.CSSProperties = {
        ...style,
        left: (style.left as number) + columnIndex * GAP,
        top: (style.top as number) + rowIndex * GAP,
        width: CELL,
        height: CELL,
    };

    if (!item) return <Box style={cellStyle} />;

    const isSelected = selectedSet.has(item.id);

    return (
        <Box style={cellStyle} sx={{ borderRadius: 1, overflow: 'hidden' }}>
            <VaultTile
                subscriber={subscriber}
                item={item}
                isSelected={isSelected}
                handleMediaClicked={() => handleMediaClicked(item)}
                allowDownload={allowDownload}
            />
        </Box>
    );
};

const VaultGrid = React.memo(
    function VaultGrid({
        itemData,
        rowCount,
        columnCount,
        columnWidth,
        rowHeight,
        width,
        height,
        onItemsRendered,
        itemKey,
    }: {
        itemData: CellData;
        rowCount: number;
        columnCount: number;
        columnWidth: number;
        rowHeight: number;
        width: number;
        height: number;
        onItemsRendered: (args: GridOnItemsRenderedProps) => void;
        itemKey: (args: { columnIndex: number; rowIndex: number; data: CellData }) => React.Key;
    }) {
        const Outer = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => <div ref={ref} {...props} />);
        const theme = useTheme();
        const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

        return (
            <GridVirt
                columnCount={columnCount}
                columnWidth={columnWidth}
                height={height}
                rowCount={rowCount}
                rowHeight={rowHeight}
                width={width}
                onItemsRendered={onItemsRendered}
                itemData={itemData}
                itemKey={itemKey}
                style={{
                    overflowX: 'hidden',
                }}
            >
                {CellRenderer}
            </GridVirt>
        );
    },
    (prev, next) => {
        return (
            prev.rowCount === next.rowCount &&
            prev.columnCount === next.columnCount &&
            prev.columnWidth === next.columnWidth &&
            prev.rowHeight === next.rowHeight &&
            prev.width === next.width &&
            prev.height === next.height &&
            prev.itemData === next.itemData &&
            prev.onItemsRendered === next.onItemsRendered &&
            prev.itemKey === next.itemKey
        );
    },
);

// Styled drop zone overlay
const DropZoneOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    border: `3px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
}));

export default function VaultMenu({
    subscriber,
    buttonText = 'Open Vault',
    onItemClick,
    editOnly = false,
    selectedItems,
    selectAllItems,
    clearAllItems,
    setOpen,
    onOpenChange,
    setSelectedAlbum,
    refreshContent = false,
    fillHeight = false,
    selectionActions,
}: VaultMenuProps) {
    const userContext = useContext(UserContext);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();
    const { addToQueue } = useDownloadManager();

    const allowDownload = isSextforceActive(subscriber) || userContext?.isAdmin;

    const {
        data: vaultAlbums,
        fetchNextPage: fetchNextPageAlbums,
        hasNextPage: vaultAlbumHasNextPage,
        isFetchingNextPage: vaultAlbumIsFetchingNextPage,
        refetch: refetchAlbums,
        isLoading: vaultAlbumsLoading,
        isRefetching: vaultAlbumIsRefetching,
        clearCacheForVaultAlbumRefresh,
    } = useSubscriberVaultAlbums(true);
    const queryClient = useQueryClient();

    const { data: onlyFansInit, allowedUploadTypes } = useSubscriberInit();

    // Upload hooks and state
    const {
        createSignKeyForFile,
        createSignedUpload,
        sendUploadDataToConverter,
        uploadFileToSignedUrl,
        sendUploadProcessedUploadDataToUser,
        checkVaultMediaHashExists,
        finishSignedMultipartUpload,
        initializeProgress,
        currentFileIndex,
        currentFileName,
        totalFiles,
        updateCurrentFile,
        abortUpload,
        resetUpload,
        uploading,
        progress,
        eta,
        uploadSpeed,
        error: uploadError,
    } = useSubscriberUpload();

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [isUploaadInProgress, setIsUploadInProgress] = useState(false);
    const [uploadState, setUploadState] = useState<string>('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [searchParams, setSearchParams] = useSearchParams();

    const [sortAlbums, setSortAlbums] = useState<boolean>(true);
    const [sortedAlbums, setSortedAlbums] = useState<OnlyFansVaultAlbums.List[] | undefined>([]);
    const [vaultAlbum, setVaultAlbum] = useState<number>(() => {
        // Pre-select the album from the URL (?album=123) if present and valid
        const albumFromUrl = parseInt(searchParams.get('album') ?? '', 10);
        return Number.isInteger(albumFromUrl) && albumFromUrl > 0 ? albumFromUrl : 0;
    });
    const [vaultAlbumName, setVaultAlbumName] = useState<string | undefined>();

    const [mediaType, setMediaType] = useState<OnlyFansVaultMedias.MediaType | null>(null);

    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [isQueueingAlbumDownload, setIsQueueingAlbumDownload] = useState<boolean>(false);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleReloadVaultAlbums = async () => {
        refreshVaultAlbums();
        handleMenuClose();
    };

    const refreshVaultAlbums = async () => {
        await clearCacheForVaultAlbumRefresh();
        // Drop the cached pages before refetching. A refetch on an infinite query keeps the
        // old data until every page has reloaded, so sortedAlbums (and the loading count)
        // would otherwise show the stale albums for the whole refresh. Removing the query
        // puts the hook into isLoading, refetch loads page one, and the next-page effect
        // pulls the rest progressively.
        queryClient.removeQueries('vaultAlbums');
        setSortedAlbums([]);
        await refetchAlbums();
        handleMenuClose();
    };

    useEffect(() => {
        refetchMedias();
    }, [refreshContent]);

    const serviceName = 'all';
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const sortMenuOpen = Boolean(anchorEl);
    const [goToDateDialogOpen, setGoToDateDialogOpen] = useState(false);
    const [sortDate, setSortDate] = useState<Date | null>(null);
    const [sortType, setSortType] = useState<OnlyFansVaultMedias.SortType>(OnlyFansVaultMedias.SortType.Recent);
    const [sortOrder, setSortOrder] = useState<OnlyFansVaultMedias.SortOrder>(OnlyFansVaultMedias.SortOrder.Desc);
    const [queryStr, setQueryStr] = useState<string>('');
    const [queryStrDebounce, setQueryStrDebounce] = useState<string>('');

    const onGoToDate = (date: Date | null) => {
        setSortDate(date);
        setGoToDateDialogOpen(false);
        handleSortMenuClose();
    };

    const handleSortMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSortMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAlbumChange = useCallback(
        (e: SelectChangeEvent) => {
            const value = parseInt(e.target.value as string, 10);
            if (vaultAlbum !== value) {
                setVaultAlbum(value);

                if (value !== -1) {
                    setVaultAlbumName(value === 0 ? 'All' : sortedAlbums?.find(item => item.id === value)?.name);
                } else {
                    setVaultAlbumName(undefined);
                }

                // Reflect the selected album in the URL so it can be shared/restored
                setSearchParams(
                    prev => {
                        const next = new URLSearchParams(prev);
                        if (Number.isInteger(value) && value > 0) {
                            next.set('album', value.toString());
                        } else {
                            next.delete('album');
                        }
                        return next;
                    },
                    { replace: true },
                );
            }
        },
        [vaultAlbum, sortedAlbums, setSearchParams],
    );

    const processedAlbums = useMemo(() => {
        if (!vaultAlbums?.pages) return [];
        const allAlbums = vaultAlbums.pages.flatMap(page => page.list);
        if (!sortAlbums) return allAlbums;
        return [...allAlbums].sort((a, b) => a.name.localeCompare(b.name));
    }, [vaultAlbums, sortAlbums]);

    useEffect(() => {
        if (setSelectedAlbum) setSelectedAlbum(vaultAlbum);
    }, [vaultAlbum]);

    useEffect(() => {
        setSortedAlbums(processedAlbums);
    }, [processedAlbums]);

    // Resolve the album name once the albums list loads when the album was pre-selected from the URL
    useEffect(() => {
        if (vaultAlbum > 0 && !vaultAlbumName) {
            const name = processedAlbums.find(album => album.id === vaultAlbum)?.name;
            if (name) setVaultAlbumName(name);
        }
    }, [processedAlbums, vaultAlbum, vaultAlbumName]);

    useEffect(() => {
        if (vaultAlbumHasNextPage && !vaultAlbumIsFetchingNextPage) {
            fetchNextPageAlbums();
        }
    }, [vaultAlbumHasNextPage, vaultAlbumIsFetchingNextPage, fetchNextPageAlbums]);

    const {
        data: vaultMediasRaw,
        isLoading: vaultAlbumLoading,
        isFetching: vaultAlbumFetching,
        refetch: refetchMedias,
        hasNextPage: vaultMediasHasNextPage,
        fetchNextPage: fetchNextVaultMediasPage,
    } = useSubscriberVaultMedias(
        serviceName,
        vaultAlbum,
        mediaType,
        sortDate,
        sortType,
        sortOrder,
        vaultAlbum !== -1 ? true : false,
        queryStr,
    );

    const pages = vaultMediasRaw?.pages as Array<{ list: VaultMediaResponseList[]; hasMore?: boolean }> | undefined;

    const items = useMemo(() => {
        return pages ? pages.flatMap(p => p.list ?? []) : [];
    }, [pages]);

    const hasMore = useMemo(() => {
        return pages ? Boolean(pages[pages.length - 1]?.hasMore) : false;
    }, [pages]);

    const handleDownloadAlbum = async () => {
        handleMenuClose();

        try {
            await dialog.confirm({
                message: `Download the whole "${
                    vaultAlbumName ?? ''
                }" album? Any remaining pages will be loaded first, then all downloadable media will be added to the download queue.`,
                title: 'Download Album',
                ok: {
                    color: 'primary',
                    text: 'Download',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            });
        } catch {
            return;
        }

        setIsQueueingAlbumDownload(true);

        try {
            let latestPages: Array<{ list: VaultMediaResponseList[]; hasMore?: boolean }> = pages ?? [];
            let more = hasMore;

            // Load the remainder of the album one page at a time
            while (more) {
                // eslint-disable-next-line no-await-in-loop
                const res = await fetchNextVaultMediasPage();
                latestPages = res.data?.pages ?? latestPages;
                const lastPage = latestPages[latestPages.length - 1];
                more = Boolean(res.hasNextPage) && Boolean(lastPage?.hasMore);
            }

            const validMedias = latestPages.flatMap(page => page.list ?? []).filter(media => media.full && media.full.url);

            validMedias.forEach(media => {
                addToQueue({
                    // eslint-disable-next-line no-underscore-dangle
                    subscriberId: subscriber._id!,
                    username: subscriber.username,
                    media,
                });
            });

            if (validMedias.length > 0) {
                enqueueSnackbar(`${validMedias.length} media item(s) added to download queue`, { variant: 'success' });
            } else {
                enqueueSnackbar('No downloadable media found in this album', { variant: 'warning' });
            }
        } finally {
            setIsQueueingAlbumDownload(false);
        }
    };

    const handleMediaClicked = (albumMedia: VaultMediaResponseList) => {
        onItemClick?.(albumMedia);
    };

    const selectAll = () => {
        selectAllItems?.(items.slice(0, 40));
    };

    const clearSelectedItems = () => {
        clearAllItems?.();
    };

    React.useEffect(() => {
        if (!open) return;
        if (!vaultMediasRaw && !vaultAlbumLoading) {
            void refetchMedias();
        }
    }, [open]);

    const handleChangeMediaType = (mediaType: OnlyFansVaultMedias.MediaType | null) => {
        setMediaType(mediaType);
    };

    const handleMediaTypeToggle = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
        if (newValue === null) return;
        if (newValue === 'all') {
            handleChangeMediaType(null);
        } else {
            handleChangeMediaType(newValue as OnlyFansVaultMedias.MediaType);
        }
    };

    // Upload functions
    const ALLOWED_TYPES = withHeicUploadTypes(onlyFansInit && allowedUploadTypes.length > 0 ? allowedUploadTypes : FALLBACK_UPLOAD_TYPES);

    const applyUploadFileLimit = (
        currentFiles: File[],
        newValidFiles: File[],
        mode: 'replace' | 'append',
    ): { files: File[]; skippedCount: number } => {
        if (mode === 'replace') {
            const files = newValidFiles.slice(0, MAX_VAULT_UPLOAD_FILES);
            return { files, skippedCount: newValidFiles.length - files.length };
        }

        const remainingSlots = MAX_VAULT_UPLOAD_FILES - currentFiles.length;
        if (remainingSlots <= 0) {
            return { files: currentFiles, skippedCount: newValidFiles.length };
        }

        const filesToAdd = newValidFiles.slice(0, remainingSlots);
        return {
            files: [...currentFiles, ...filesToAdd],
            skippedCount: newValidFiles.length - filesToAdd.length,
        };
    };

    const notifySkippedUploadFiles = (skippedCount: number) => {
        if (skippedCount > 0) {
            enqueueSnackbar(`Maximum of ${MAX_VAULT_UPLOAD_FILES} files per upload. ${skippedCount} file(s) were not added.`, {
                variant: 'warning',
                autoHideDuration: 6000,
            });
        }
    };

    const validateFiles = (files: File[]): File[] => {
        return files.filter(file => isAllowedUploadFile(file, ALLOWED_TYPES));
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const validFiles = validateFiles(Array.from(files));
            if (validFiles.length > 0) {
                const mode = uploadDialogOpen ? 'append' : 'replace';
                const { files: nextFiles, skippedCount } = applyUploadFileLimit(uploadDialogOpen ? filesToUpload : [], validFiles, mode);
                notifySkippedUploadFiles(skippedCount);
                setFilesToUpload(nextFiles);
                setUploadDialogOpen(true);
            } else {
                enqueueSnackbar('No valid media files selected. Please select images, videos, or audio files.', {
                    variant: 'error',
                    autoHideDuration: 6000,
                });
            }
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAddMoreFiles = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = (fileToRemove: File) => {
        setFilesToUpload(prev => prev.filter(file => file !== fileToRemove));
    };

    const handleFilesDroppedInDialog = (droppedFiles: File[]) => {
        const validFiles = validateFiles(droppedFiles);
        if (validFiles.length > 0) {
            const { files, skippedCount } = applyUploadFileLimit(filesToUpload, validFiles, 'append');
            notifySkippedUploadFiles(skippedCount);
            setFilesToUpload(files);
        } else {
            enqueueSnackbar('No valid media files dropped. Please drop images, videos, or audio files.', {
                variant: 'error',
                autoHideDuration: 6000,
            });
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set dragging to false if leaving the container
        if (e.currentTarget === e.target) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        const validFiles = validateFiles(files);

        if (validFiles.length > 0) {
            const { files, skippedCount } = applyUploadFileLimit([], validFiles, 'replace');
            notifySkippedUploadFiles(skippedCount);
            setFilesToUpload(files);
            setUploadDialogOpen(true);
        } else {
            enqueueSnackbar('No valid media files dropped. Please drop images, videos, or audio files.', {
                variant: 'error',
                autoHideDuration: 6000,
            });
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = async (filesWithMetadata: UploadFileMetadata[], albumId: number) => {
        if (!onlyFansInit || !onlyFansInit.init) {
            enqueueSnackbar('Failed to initialize upload. Please try again.', {
                variant: 'error',
                autoHideDuration: 6000,
            });

            return;
        }

        try {
            // Initialize progress tracking for all files
            const allFiles = filesWithMetadata.map(f => f.file);
            initializeProgress(allFiles, 0);

            setIsUploadInProgress(true);
            let uploadedBytes = 0; // Track cumulative bytes uploaded

            const processedUploads: {
                fileName: string;
                name: string;
                description: string;
                taggedCreators: any; // Replace 'any' with the appropriate type
                taggedReleaseForms: any; // Replace 'any' with the appropriate type
                processedMedia: OnlyFansFileUploadResponse.Response;
            }[] = [];

            for (let i = 0; i < filesWithMetadata.length; i++) {
                const { file, name, description, taggedCreators, taggedReleaseForms } = filesWithMetadata[i];

                // Update current file tracking
                updateCurrentFile(file.name, i);
                setUploadState(`Uploading ${file.name} (${i + 1}/${filesWithMetadata.length})...`);

                const { fileName, signKey, mimetype } = createSignKeyForFile(onlyFansInit.init, file.name, resolveUploadMimeType(file));

                const E = 5242880; // 5MB
                const parts: number = Math.floor(file.size / E) + 1;

                const signedUpload: OnlyFansFileUploadResponse.CreateSignedUpload | null = await createSignedUpload(
                    fileName,
                    signKey,
                    mimetype,
                    parts,
                );

                if (!signedUpload) {
                    throw new Error(`Failed to create signed upload for ${file.name}`);
                }

                if (signedUpload.putUrl) {
                    // Single part upload
                    const partId: string = `${file.name}-single`;
                    const eTag: string | null = await uploadFileToSignedUrl(signedUpload.putUrl, file, partId);

                    uploadedBytes += file.size;

                    if (!eTag) {
                        throw new Error(`Failed to upload ${file.name}`);
                    }

                    // Step 4: Send upload data to converter
                    setUploadState(`Processing ${file.name}...`);

                    const uploadResponse: OnlyFansFileUploadResponse.Response | null = await sendUploadDataToConverter({
                        fileName,
                        signKey,
                        mimetype,
                        location: signedUpload.getUrl,
                        eTag,
                        uploadToken: signedUpload.uploadToken ?? null,
                    });

                    if (!uploadResponse) {
                        throw new Error(`Failed to process upload for ${file.name}`);
                    }

                    processedUploads.push({
                        fileName,
                        name,
                        description,
                        taggedCreators,
                        taggedReleaseForms,
                        processedMedia: uploadResponse,
                    });
                } else {
                    // Multi-part upload
                    const { keys, uploadId } = signedUpload;

                    if (!keys || keys.length === 0 || !uploadId) {
                        throw new Error(`No upload parts returned for multi-part upload of ${file.name}`);
                    }

                    setUploadState(`Uploading ${file.name}...`);

                    const limit = pLimit(10);

                    // Upload all parts simultaneously
                    const partUploadPromises = keys.map(keyPart =>
                        limit(async () => {
                            const partNumber: number = keyPart.part;
                            const putUrl: string = keyPart.putUrl;

                            const startByte: number = (partNumber - 1) * E;
                            const endByte: number = Math.min(startByte + E, file.size);
                            const fileSlice: Blob = file.slice(startByte, endByte);

                            const partId: string = `${file.name}-part${partNumber}`;
                            const eTag: string | null = await uploadFileToSignedUrl(putUrl, fileSlice, partId);

                            if (!eTag) {
                                throw new Error(`Failed to upload part ${partNumber} for ${file.name}`);
                            }

                            return { PartNumber: partNumber, ETag: eTag };
                        }),
                    );

                    // Wait for all parts to complete
                    const partETags: {
                        PartNumber: number;
                        ETag: string;
                    }[] = await Promise.all(partUploadPromises);

                    // Sort by part number for S3
                    partETags.sort((a, b) => a.PartNumber - b.PartNumber);

                    uploadedBytes += file.size;

                    const finishResult: OnlyFansFileUploadResponse.FinishResponse | null = await finishSignedMultipartUpload(
                        uploadId,
                        signKey,
                        partETags,
                        false,
                    );

                    if (!finishResult || !finishResult.ETag) {
                        throw new Error(`Failed to finalize multi-part upload for ${file.name}`);
                    }

                    const uploadResponse: OnlyFansFileUploadResponse.Response | null = await sendUploadDataToConverter({
                        fileName,
                        signKey,
                        mimetype,
                        location: signedUpload.getUrl,
                        eTag: finishResult.ETag,
                    });

                    if (!uploadResponse) {
                        throw new Error(`Failed to process upload for ${file.name}`);
                    }

                    processedUploads.push({
                        fileName,
                        name,
                        description,
                        taggedCreators,
                        taggedReleaseForms,
                        processedMedia: uploadResponse,
                    });
                }
            }

            // Step 5: Send uploaded media to OnlyFans user
            setUploadState('Finalizing upload...');

            const sendResult: {
                success: boolean;
                addToAlbumSuccess: boolean;
            } = await sendUploadProcessedUploadDataToUser(albumId, processedUploads);

            if (!sendResult || !sendResult.success) {
                throw new Error('Failed to send processed upload data to user');
            }

            enqueueSnackbar(`Successfully uploaded ${filesWithMetadata.length} file(s)`, {
                variant: 'success',
                autoHideDuration: 6000,
            });

            setUploadState('');
            setUploadDialogOpen(false);
            setFilesToUpload([]);
            resetUpload();

            // Refresh the vault content
            await refetchMedias();
        } catch (error: any) {
            console.error('Upload error:', error);
            setIsUploadInProgress(false);
            setUploadState('');

            enqueueSnackbar(error.message || 'Upload failed', {
                variant: 'error',
                autoHideDuration: 6000,
            });

            abortUpload();
            resetUpload();
        } finally {
            setIsUploadInProgress(false);
        }
    };

    const handleAbortUpload = () => {
        abortUpload();
        setIsUploadInProgress(false);
        setUploadState('');
        setUploadDialogOpen(false);
        setFilesToUpload([]);
        resetUpload();
    };

    const handleUploadDialogClose = () => {
        if (!uploading) {
            setIsUploadInProgress(false);
            setUploadState('');
            setUploadDialogOpen(false);
            setFilesToUpload([]);
            resetUpload();
        }
    };

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let rafId: number | null = null;

        const resizeObserver = new ResizeObserver(entries => {
            if (rafId) cancelAnimationFrame(rafId);

            rafId = requestAnimationFrame(() => {
                for (const entry of entries) {
                    const newWidth = Math.floor(entry.contentRect.width);
                    setContainerWidth(prev => {
                        if (Math.abs(prev - newWidth) > 2) {
                            return newWidth;
                        }
                        return prev;
                    });

                    const newHeight = Math.floor(entry.contentRect.height);
                    setContainerHeight(prev => {
                        if (Math.abs(prev - newHeight) > 2) {
                            return newHeight;
                        }
                        return prev;
                    });
                }
            });
        });

        resizeObserver.observe(container);

        const initialWidth = Math.floor(container.clientWidth);
        setContainerWidth(initialWidth);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, []);

    const MIN_CELL = fullScreen ? 100 : 150;
    const GAP = 1;

    const COLS = useMemo(() => {
        if (containerWidth === 0) return fullScreen ? 3 : 6;
        return Math.max(1, Math.floor(containerWidth / (MIN_CELL + GAP)));
    }, [containerWidth, MIN_CELL, GAP, fullScreen]);

    const CELL = useMemo(() => {
        if (containerWidth === 0) return MIN_CELL;
        const availableWidth = containerWidth - (COLS - 1) * GAP;
        return Math.floor(availableWidth / COLS);
    }, [containerWidth, COLS, GAP, MIN_CELL]);

    const GRID_WIDTH = useMemo(() => {
        const calculatedWidth = COLS * CELL + (COLS - 1) * GAP;
        return Math.min(calculatedWidth, containerWidth);
    }, [COLS, CELL, GAP, containerWidth]);

    const rows = Math.ceil(items.length / COLS);
    const totalRows = hasMore ? rows + 1 : rows;
    const fallbackGridHeight = fullScreen
        ? Math.min(Math.max(420, Math.round(window.innerHeight * 0.75)), 720)
        : Math.round(window.innerHeight * 0.75);
    const gridHeight = fillHeight && containerHeight > 0 ? containerHeight : fallbackGridHeight;

    const selectedSet = useMemo(() => new Set((selectedItems ?? []).map(s => s.id)), [selectedItems]);
    const selectedCount = selectedItems?.length ?? 0;

    const itemData = useMemo(
        () => ({
            subscriber,
            items,
            selectedSet,
            allowDownload,
            handleMediaClicked,
            CELL,
            GAP,
            COLS,
        }),
        [items, selectedSet, subscriber, allowDownload, handleMediaClicked, CELL, GAP, COLS],
    );

    const itemKey = useCallback(({ columnIndex, rowIndex, data }: { columnIndex: number; rowIndex: number; data: CellData }) => {
        const idx = rowIndex * data.COLS + columnIndex;
        return data.items[idx]?.id ?? `skeleton-${rowIndex}-${columnIndex}`;
    }, []);

    const handleItemsRendered = React.useCallback(
        ({ visibleRowStopIndex }: GridOnItemsRenderedProps) => {
            if (!hasMore || vaultAlbumFetching) return;
            if (visibleRowStopIndex >= totalRows - 12) {
                void fetchNextVaultMediasPage();
            }
        },
        [hasMore, totalRows, vaultAlbumFetching, fetchNextVaultMediasPage],
    );

    const setQueryStrValue = (value: string) => {
        setQueryStr(value);
    };

    const searchQueryDebounce = useCallback(debounce(setQueryStrValue, 750), []);

    const searchOnChange = (value: string) => {
        searchQueryDebounce(value);
        setQueryStrDebounce(value);
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept={buildUploadAcceptAttr(ALLOWED_TYPES)}
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />

            <Card
                sx={{
                    width: '100%',
                    position: 'relative',
                    ...(fillHeight && {
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }),
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {isDragging && (
                    <DropZoneOverlay>
                        <Stack alignItems="center" spacing={2}>
                            <UploadIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                            <Typography variant="h5" color="primary">
                                Drop files here to upload
                            </Typography>
                        </Stack>
                    </DropZoneOverlay>
                )}

                <Stack direction={'column'} sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
                    <Box p={2} sx={{ width: '100%' }}>
                        <Grid container spacing={1} justifyContent="center" alignItems="center">
                            <Grid item xs={12}>
                                <Grid container spacing={0.5} alignItems="center">
                                    <Grid item xs="auto">
                                        <Tooltip title={isSearchOpen ? 'Close search' : 'Open search'}>
                                            <span>
                                                <IconButton
                                                    onClick={() => setIsSearchOpen(prev => !prev)}
                                                    color={isSearchOpen ? 'primary' : 'default'}
                                                >
                                                    {isSearchOpen ? <CloseIcon /> : <SearchIcon />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs>
                                        <Grid container spacing={1} alignItems="center" wrap={fullScreen ? 'wrap' : 'nowrap'}>
                                            <Grid
                                                item
                                                sx={{
                                                    display: !isSearchOpen && fullScreen ? 'none' : 'block',
                                                    flexBasis: isSearchOpen ? (fullScreen ? '100%' : '70%') : 0,
                                                    flexGrow: isSearchOpen ? (fullScreen ? 0 : 1) : 0,
                                                    flexShrink: isSearchOpen ? (fullScreen ? 0 : 1) : 0,
                                                    opacity: isSearchOpen ? 1 : 0,
                                                    overflow: 'hidden',
                                                    mr: isSearchOpen ? 1 : 0,
                                                    transition: 'flex-basis 0.3s ease, opacity 0.2s ease, margin-right 0.3s ease',
                                                }}
                                            >
                                                <TextField
                                                    label="Search"
                                                    value={queryStrDebounce}
                                                    onChange={e => {
                                                        searchOnChange(e.target.value);
                                                    }}
                                                    variant="outlined"
                                                    placeholder="Search..."
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{
                                                        endAdornment: queryStr && (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setQueryStr('');
                                                                        setQueryStrDebounce('');
                                                                    }}
                                                                    edge="end"
                                                                    size="small"
                                                                >
                                                                    <CloseIcon sx={{ color: '#888' }} />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                        sx: {
                                                            transition: 'all 0.5s ease',
                                                        },
                                                    }}
                                                    sx={{
                                                        mt: 0,
                                                        mb: 0,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                sx={{
                                                    flexBasis: isSearchOpen && !fullScreen ? '30%' : '100%',
                                                    flexGrow: isSearchOpen && !fullScreen ? 0 : 1,
                                                    flexShrink: isSearchOpen && !fullScreen ? 0 : 1,
                                                    transition: 'flex-basis 0.3s ease, flex-grow 0.3s ease',
                                                }}
                                            >
                                                <AlbumSelect
                                                    vaultAlbum={vaultAlbum}
                                                    sortedAlbums={sortedAlbums}
                                                    isLoading={vaultAlbumsLoading || vaultAlbumIsRefetching}
                                                    handleAlbumChange={handleAlbumChange}
                                                    selectSize="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs="auto">
                                        <Tooltip title="Upload files">
                                            <span>
                                                <IconButton onClick={handleUploadClick} color="primary">
                                                    <UploadIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs="auto">
                                        <Tooltip title="More options">
                                            <span>
                                                <IconButton onClick={handleMenuOpen} color="secondary">
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        <Menu
                                            anchorEl={menuAnchorEl}
                                            open={Boolean(menuAnchorEl)}
                                            onClose={handleMenuClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            <MenuItem
                                                onClick={handleReloadVaultAlbums}
                                                disabled={vaultAlbumsLoading || vaultAlbumIsRefetching}
                                            >
                                                <ListItemIcon>
                                                    <RefreshIcon color="secondary" />
                                                </ListItemIcon>
                                                Refresh Vault Album List
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    refetchMedias();
                                                    handleMenuClose();
                                                }}
                                                disabled={vaultAlbumLoading || vaultAlbumFetching}
                                            >
                                                <ListItemIcon>
                                                    <RefreshIcon color="secondary" />
                                                </ListItemIcon>
                                                Refresh Vault Content
                                            </MenuItem>
                                            <MenuItem
                                                onClick={handleDownloadAlbum}
                                                disabled={
                                                    !allowDownload ||
                                                    !Number.isInteger(vaultAlbum) ||
                                                    vaultAlbum <= 0 ||
                                                    vaultAlbumLoading ||
                                                    vaultAlbumFetching ||
                                                    isQueueingAlbumDownload
                                                }
                                            >
                                                <ListItemIcon>
                                                    <DownloadRoundedIcon color="secondary" />
                                                </ListItemIcon>
                                                Download Album
                                            </MenuItem>
                                        </Menu>

                                        {setOpen && (
                                            <IconButton edge="end" onClick={() => setOpen(false)} aria-label="close">
                                                <CloseIcon />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems={'center'} justifyContent="space-between">
                                    <Grid item>
                                        <ToggleButtonGroup
                                            value={mediaType ?? 'all'}
                                            color="primary"
                                            exclusive
                                            onChange={handleMediaTypeToggle}
                                            size="small"
                                        >
                                            <ToggleButton value="all">
                                                <CollectionsIcon fontSize="small" />
                                            </ToggleButton>
                                            <ToggleButton value={OnlyFansVaultMedias.MediaType.Photo}>
                                                <ImageIcon fontSize="small" />
                                            </ToggleButton>
                                            <ToggleButton value={OnlyFansVaultMedias.MediaType.Video}>
                                                <VideocamIcon fontSize="small" />
                                            </ToggleButton>
                                            <ToggleButton value={OnlyFansVaultMedias.MediaType.Audio}>
                                                <AudiotrackIcon fontSize="small" />
                                            </ToggleButton>
                                            <ToggleButton value={OnlyFansVaultMedias.MediaType.GIF}>
                                                <GifBoxIcon fontSize="small" />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>

                                    <Grid item xs="auto">
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {!editOnly && (
                                                <>
                                                    <Grid item xs="auto">
                                                        <Tooltip title="Select All">
                                                            <span>
                                                                <IconButton
                                                                    onClick={async () => {
                                                                        selectAll();
                                                                    }}
                                                                    disabled={vaultAlbumLoading || vaultAlbumFetching}
                                                                    color="primary"
                                                                >
                                                                    <DoneAllIcon fontSize="medium" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Grid>

                                                    <Grid item xs="auto">
                                                        <Tooltip title="Clear Selection">
                                                            <span>
                                                                <IconButton
                                                                    color="error"
                                                                    onClick={() => {
                                                                        clearSelectedItems();
                                                                    }}
                                                                    disabled={
                                                                        vaultAlbumLoading ||
                                                                        vaultAlbumFetching ||
                                                                        !selectedItems ||
                                                                        selectedItems.length === 0
                                                                    }
                                                                >
                                                                    <ClearIcon fontSize="medium" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Grid>
                                                </>
                                            )}
                                            <Grid item xs="auto">
                                                <MessageUsersFormAddMediasSelectorGoToDateDialog
                                                    open={goToDateDialogOpen}
                                                    onClose={() => {
                                                        setGoToDateDialogOpen(false);
                                                        handleSortMenuClose();
                                                    }}
                                                    date={sortDate}
                                                    onGoToDate={onGoToDate}
                                                />
                                                <Tooltip title="Sort">
                                                    <span>
                                                        <IconButton
                                                            onClick={handleSortMenuClick}
                                                            disabled={vaultAlbumLoading || vaultAlbumFetching}
                                                            color="info"
                                                        >
                                                            <SortIcon fontSize="medium" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <StyledLocalMenu
                                                    id="album-sort-menu"
                                                    MenuListProps={{ 'aria-labelledby': 'album-sort-button' }}
                                                    anchorEl={anchorEl}
                                                    open={sortMenuOpen}
                                                    onClose={handleSortMenuClose}
                                                >
                                                    <MenuItem
                                                        onClick={() => {
                                                            setSortDate(null);
                                                            handleSortMenuClose();
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortDate ? <RadioButtonUncheckedIcon /> : <RadioButtonCheckedIcon />} All Time
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setGoToDateDialogOpen(true);
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortDate ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                                                        {sortDate ? moment(sortDate).format('L') : 'Go to date'}
                                                    </MenuItem>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <MenuItem
                                                        onClick={() => {
                                                            setSortType(OnlyFansVaultMedias.SortType.Recent);
                                                            handleSortMenuClose();
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortType === 'recent' ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                                                        Recent
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setSortType(OnlyFansVaultMedias.SortType.MostLiked);
                                                            handleSortMenuClose();
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortType === 'most-liked' ? (
                                                            <RadioButtonCheckedIcon />
                                                        ) : (
                                                            <RadioButtonUncheckedIcon />
                                                        )}
                                                        Most liked
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setSortType(OnlyFansVaultMedias.SortType.HighestTips);
                                                            handleSortMenuClose();
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortType === 'highest-tips' ? (
                                                            <RadioButtonCheckedIcon />
                                                        ) : (
                                                            <RadioButtonUncheckedIcon />
                                                        )}
                                                        Highest tips
                                                    </MenuItem>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <MenuItem
                                                        onClick={() => {
                                                            setSortOrder(OnlyFansVaultMedias.SortOrder.Asc);
                                                            handleSortMenuClose();
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortOrder === 'asc' ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                                                        Ascending
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setSortOrder(OnlyFansVaultMedias.SortOrder.Desc);
                                                            handleSortMenuClose();
                                                        }}
                                                        disableRipple
                                                    >
                                                        {sortOrder === 'desc' ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                                                        Descending
                                                    </MenuItem>
                                                </StyledLocalMenu>
                                            </Grid>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            ...(fillHeight && { flex: 1, minHeight: 0 }),
                        }}
                    >
                        {!vaultAlbumLoading && !vaultAlbumFetching && <Divider />}
                        {vaultAlbumLoading && items.length === 0 && <LinearProgress sx={{ height: '2px ' }} />}
                        {vaultAlbumFetching && <LinearProgress sx={{ height: '2px ' }} />}
                        <Box
                            ref={containerRef}
                            sx={{
                                background: theme.palette.grey[50],
                                pt: 2,
                                pb: 0,
                                pl: 1,
                                pr: 1,
                                ...(fillHeight
                                    ? {
                                          // The virtualized grid inside is the only scroll region
                                          flex: 1,
                                          minHeight: 0,
                                          position: 'relative',
                                          overflow: 'hidden',
                                      }
                                    : {
                                          overflow: 'auto',
                                          overflowX: 'hidden',
                                      }),
                            }}
                        >
                            {containerWidth > 0 && (
                                <Box
                                    sx={{
                                        width: GRID_WIDTH,
                                        height: gridHeight,
                                        position: 'relative',
                                        mx: 'auto',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <VaultGrid
                                        itemData={itemData}
                                        rowCount={totalRows}
                                        columnCount={COLS}
                                        columnWidth={CELL + GAP}
                                        rowHeight={CELL + GAP}
                                        width={GRID_WIDTH}
                                        height={gridHeight}
                                        onItemsRendered={handleItemsRendered}
                                        itemKey={itemKey}
                                    />
                                </Box>
                            )}

                            {!vaultAlbumLoading && hasMore && (
                                <ExpandCircleDownIcon
                                    fontSize="large"
                                    sx={{
                                        display: 'block',
                                        mx: 'auto',
                                        color: 'gray',
                                        animation: 'bounce 2s infinite',
                                        '@keyframes bounce': {
                                            '0%, 100%': { transform: 'translateY(0)' },
                                            '50%': { transform: 'translateY(10%)' },
                                        },
                                        ...(fillHeight
                                            ? {
                                                  position: 'absolute',
                                                  bottom: 8,
                                                  left: 0,
                                                  right: 0,
                                                  pointerEvents: 'none',
                                              }
                                            : {
                                                  position: 'relative',
                                                  top: -10,
                                              }),
                                    }}
                                />
                            )}
                        </Box>

                        {!vaultAlbumLoading && items.length === 0 && (
                            <Typography variant="body1" align="center" color={'text.secondary'}>
                                Album empty
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Card>

            {/* Hovering selection bar: floats over the grid while items are selected.
                Inside a fixed-height host (dialog) it pins to the bottom of the card;
                on a page it pins to the bottom of the viewport. */}
            <Slide direction="up" in={!editOnly && selectedCount > 0} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        position: fillHeight ? 'absolute' : 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: t => t.zIndex.drawer + 1,
                        display: 'flex',
                        justifyContent: 'center',
                        pointerEvents: 'none', // so clicks only go to the Paper
                    }}
                >
                    <Paper
                        elevation={6}
                        sx={{
                            pointerEvents: 'auto',
                            width: '100%',
                            maxWidth: 1200,
                            mx: fillHeight ? 2 : 'auto',
                            mb: 1.5,
                            borderRadius: 3,
                            px: 2,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography variant="body2">
                            {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {selectionActions}
                            <Tooltip title="Clear Selection">
                                <IconButton color="error" onClick={clearSelectedItems} size="large">
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Paper>
                </Box>
            </Slide>

            <VaultUploadDialog
                subscriber={subscriber}
                open={uploadDialogOpen}
                files={filesToUpload}
                onClose={handleUploadDialogClose}
                onUpload={handleUpload}
                onAddMore={handleAddMoreFiles}
                onRemove={handleRemoveFile}
                onFilesDropped={handleFilesDroppedInDialog}
                onCheckFileExists={checkVaultMediaHashExists}
                onAbort={handleAbortUpload} // NEW
                uploading={uploading || isUploaadInProgress}
                uploadState={uploadState}
                progress={progress}
                eta={eta}
                uploadSpeed={uploadSpeed}
                currentFileName={currentFileName} // NEW
                currentFileIndex={currentFileIndex} // NEW
                totalFiles={totalFiles} // NEW
                sortedAlbums={sortedAlbums}
                isLoadingAlbums={vaultAlbumsLoading || vaultAlbumIsRefetching}
            />
        </>
    );
}
